import { AssureEditionEnum, HostingOptionEnum } from "@/app/api/battery-portal-api";
import { isLicenseAllowed, getLicenseDisabledReason } from "@/app/utils/license-helper";
// Elements that natively support the `disabled` attribute. For everything else we grey it out
// visually and intercept clicks at the capture phase so v-on handlers on the same element (or on
// children) don't fire.
// NOTE: <button> is intentionally excluded. Setting the native `disabled` attribute suppresses
// pointer events on the element in all major browsers, which prevents mouseenter/mouseover from
// firing and therefore breaks both native `title` tooltips and Bootstrap-Vue's `v-b-tooltip`.
// Buttons are still made non-interactive via `aria-disabled`, the `.license-disabled` class,
// and the capture-phase click blocker below.
const NATIVELY_DISABLABLE = new Set(["INPUT", "SELECT", "TEXTAREA", "FIELDSET", "OPTGROUP", "OPTION"]);
const CLICK_BLOCKER_KEY = "__licenseClickBlocker";
/**
 * Hosting gates (Cloud vs On-Prem) represent a different product SKU, not an upsell tier, so
 * failing a hosting rule means the feature is not part of this deployment at all and must be
 * hidden. Edition gates (Basic/Standard/Premium) are upgrade paths and should stay visible-but-
 * disabled with a tooltip so users can discover them.
 */
function isHostingGate(rule) {
    return !!rule && rule.hostingOption !== undefined;
}
function hideElement(el) {
    el.style.display = "none";
}
function applyLicenseGate(el, rule) {
    const allowed = isLicenseAllowed(rule);
    if (!allowed && isHostingGate(rule)) {
        hideElement(el);
        return;
    }
    if (allowed) {
        // Clear any prior hide from a previous evaluation.
        if (el.style.display === "none")
            el.style.display = "";
        el.classList.remove("license-disabled");
        el.removeAttribute("aria-disabled");
        el.style.opacity = "";
        el.style.cursor = "";
        el.style.pointerEvents = "";
        if (NATIVELY_DISABLABLE.has(el.tagName)) {
            el.disabled = false;
        }
        const stored = el[CLICK_BLOCKER_KEY];
        if (stored) {
            el.removeEventListener("click", stored, true);
            delete el[CLICK_BLOCKER_KEY];
        }
        // Only strip title we set; leave author-provided titles alone by tracking a sentinel.
        if (el.__licenseTitleSet) {
            el.removeAttribute("title");
            delete el.__licenseTitleSet;
        }
        return;
    }
    // Denied: keep visible but non-interactive.
    el.classList.add("license-disabled");
    el.setAttribute("aria-disabled", "true");
    el.style.opacity = "0.5";
    el.style.cursor = "not-allowed";
    if (NATIVELY_DISABLABLE.has(el.tagName)) {
        el.disabled = true;
    }
    // Block clicks in the capture phase so any v-on handler on this element (or descendants)
    // does not fire. Registered once and preserved across updates.
    if (!el[CLICK_BLOCKER_KEY]) {
        const blocker = (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
        };
        el.addEventListener("click", blocker, true);
        el[CLICK_BLOCKER_KEY] = blocker;
    }
    const reason = getLicenseDisabledReason(rule);
    if (reason && !el.getAttribute("title")) {
        el.setAttribute("title", reason);
        el.__licenseTitleSet = true;
    }
    else if (reason && el.__licenseTitleSet) {
        el.setAttribute("title", reason);
    }
}
function unmountLicenseGate(el) {
    const stored = el[CLICK_BLOCKER_KEY];
    if (stored) {
        el.removeEventListener("click", stored, true);
        delete el[CLICK_BLOCKER_KEY];
    }
}
/** Flexible directive: v-license="{ hostingOption: ..., minimumEdition: ..., batteryType: ... }" */
export const vLicense = {
    mounted(el, binding) {
        applyLicenseGate(el, binding.value);
    },
    updated(el, binding) {
        applyLicenseGate(el, binding.value);
    },
    unmounted(el) {
        unmountLicenseGate(el);
    }
};
/** Shorthand: v-cloud-only */
export const vCloudOnly = {
    mounted(el) {
        applyLicenseGate(el, { hostingOption: HostingOptionEnum.PowershieldCloud });
    },
    updated(el) {
        applyLicenseGate(el, { hostingOption: HostingOptionEnum.PowershieldCloud });
    },
    unmounted(el) {
        unmountLicenseGate(el);
    }
};
/** Shorthand: v-onprem-only */
export const vOnPremOnly = {
    mounted(el) {
        applyLicenseGate(el, { hostingOption: HostingOptionEnum.OnPrem });
    },
    updated(el) {
        applyLicenseGate(el, { hostingOption: HostingOptionEnum.OnPrem });
    },
    unmounted(el) {
        unmountLicenseGate(el);
    }
};
/** Shorthand: v-standard-or-higher */
export const vStandardOrHigher = {
    mounted(el) {
        applyLicenseGate(el, { minimumEdition: AssureEditionEnum.OnPremStandard });
    },
    updated(el) {
        applyLicenseGate(el, { minimumEdition: AssureEditionEnum.OnPremStandard });
    },
    unmounted(el) {
        unmountLicenseGate(el);
    }
};
/** Shorthand: v-premium-only */
export const vPremiumOnly = {
    mounted(el) {
        applyLicenseGate(el, { minimumEdition: AssureEditionEnum.OnPremPremium });
    },
    updated(el) {
        applyLicenseGate(el, { minimumEdition: AssureEditionEnum.OnPremPremium });
    },
    unmounted(el) {
        unmountLicenseGate(el);
    }
};
